import {
	getBackgroundRemovalWidgetGroups,
	getClosedRemoveBackgroundDialog,
	getRemoveBackgroundDialogState,
	getWidgetsOverlappingBackground,
} from '../layout/webDashboardBackgroundRemoval';

export const createBackgroundRemovalHandlers = ({
	dashboard,
	setDashboard,
	removeBackgroundDialog,
	setRemoveBackgroundDialog,
	getEditableMetrics,
	getWidgetRectPx,
	findNearestEmptySpotForWidgets,
	storeWidgetPosition,
	sortWidgets,
	setAlertMessage,
	t,
	setAlertType,
	setShowAlert,
}) => {
	const resetRemoveBackgroundDialog = () => {
		setRemoveBackgroundDialog(getClosedRemoveBackgroundDialog());
	};

	const continueRemoveBackground = () => {
		const bgId = removeBackgroundDialog?.backgroundId;
		if (!bgId) {
			resetRemoveBackgroundDialog();
			return;
		}

		const currentWidgets = dashboard?.widgets || [];
		const metrics = getEditableMetrics();
		const {
			widgetsWithoutBackground,
			stationaryWidgets,
			sortedAffectedWidgets,
		} = getBackgroundRemovalWidgetGroups({
			widgets: currentWidgets,
			backgroundId: bgId,
			affectedWidgetIds: removeBackgroundDialog?.affectedWidgetIds || [],
			metrics,
			getWidgetRectPx,
		});

		const placedWidgets = [...stationaryWidgets];
		const repositionedWidgetsById = new Map();

		for (const widget of sortedAffectedWidgets) {
			const widgetRectPx = getWidgetRectPx(widget, metrics);
			const widgetWidth = widgetRectPx.width;
			const widgetHeight = widgetRectPx.height;
			const startLeft = widgetRectPx.left;
			const startTop = widgetRectPx.top;

			const { found, left, top } = findNearestEmptySpotForWidgets(
				startLeft,
				startTop,
				widgetWidth,
				widgetHeight,
				placedWidgets
			);

			if (!found) {
				setAlertMessage(
					t.removeBackgroundNoSpace ||
					'No free area is available to reposition widgets. Please empty some area and try again.'
				);
				setAlertType('warning');
				setShowAlert(true);
				resetRemoveBackgroundDialog();
				return;
			}

			const movedWidget = storeWidgetPosition(widget, {
				left,
				top,
				width: widgetWidth,
				height: widgetHeight,
			}, metrics);
			repositionedWidgetsById.set(widget.id, movedWidget);
			placedWidgets.push(movedWidget);
		}

		const nextWidgets = widgetsWithoutBackground.map((widget) => (
			repositionedWidgetsById.get(widget.id) || widget
		));

		setDashboard({
			...dashboard,
			widgets: sortWidgets(nextWidgets),
		});

		resetRemoveBackgroundDialog();
	};

	const remove = (id) => {
		const currentWidgets = dashboard?.widgets || [];
		const widgetToRemove = currentWidgets.find((widget) => widget.id === id);
		if (!widgetToRemove) return;

		if (widgetToRemove.type !== 'background-image') {
			setDashboard({
				...dashboard,
				widgets: currentWidgets.filter((widget) => widget.id !== id),
			});
			return;
		}

		const overlappingWidgets = getWidgetsOverlappingBackground({
			backgroundWidget: widgetToRemove,
			widgetsToCheck: currentWidgets,
			metrics: getEditableMetrics(),
			getWidgetRectPx,
		});
		if (overlappingWidgets.length === 0) {
			setDashboard({
				...dashboard,
				widgets: currentWidgets.filter((widget) => widget.id !== id),
			});
			return;
		}

		setRemoveBackgroundDialog(getRemoveBackgroundDialogState({
			backgroundId: id,
			overlappingWidgets,
		}));
	};

	return {
		continueRemoveBackground,
		remove,
		resetRemoveBackgroundDialog,
	};
};
